// probably timing problem with lit: https://github.com/lit/lit/blob/7453e365000e6a289c139cf7e175a4742296333d/packages/reactive-element/src/css-tag.ts#L10
// if lit is loaded before style sheet it cannot handle CSSStyleSheets from AdornisCSS
import 'construct-style-sheets-polyfill'; // THIS HAS TO BE THE FIRST IMPORT FOR FIREFOX TO WORK CORRECTLY

import { useABAC } from '@adornis/abac/client/useABAC.js';
import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { PasswordEnforcementGate } from '@nsc/smartteams-shared/client/password-enforcement.js';
import { html } from 'lit';
import * as m from '../localization/out/messages.js';
import { getLocale } from '../localization/out/runtime.js';
import '../isomorphic-startup.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { ROUTE_PERMISSION_MAP } from '../features/permissions/roles-and-permissions.js';
import { AppNavbar } from './components/app-navbar.js';
import { AcademyPage } from './pages/academy-page.js';
import { ColleagueTicketsPage } from './pages/colleague-tickets-page.js';
import { CourseDetailPage } from './pages/course-detail-page.js';
import { CoursePlayerPage } from './pages/course-player-page.js';
import { ProfilePage } from './pages/profile-page.js';
import { SVRPage } from './pages/svr-page.js';
import { TicketDetailPage } from './pages/ticket-detail-page.js';
import { TicketOverviewPage } from './pages/ticket-overview-page.js';
import { UserManagementPage } from './pages/user-management-page.js';
import './tailwind.css' with { type: 'tailwind' };
import { APP_VERSION } from '../version.js';
import smartteamLogo from './assets/smartteam-logo.png';

// resolve + persist the locale cookie before any engine package runtime falls back to its own baseLocale
getLocale();

BaseQLClient.initDefault();
void subscribeCurrentUser();

/**
 * Browser tab title suffix. Matches the title the server renders into the initial HTML
 * (`server/startup.ts`), so the tab doesn't change name between first paint and router boot.
 */
const APP_DOCUMENT_TITLE = 'SmartTeam';

/**
 * Every route carries a `documentTitlePart`, otherwise @adornis/router falls back to the route's
 * *name* and the tab reads `colleagueTickets`. Titles reuse the labels already shown in the UI so
 * they stay translated, and the router appends ` | SmartTeam`.
 */
export const router = makeRouter(
  {
    tickets: { path: '/', render: () => TicketOverviewPage(), documentTitlePart: () => m.nav_tickets() },
    colleagueTickets: {
      path: '/colleagues',
      render: () => ColleagueTicketsPage(),
      documentTitlePart: () => m.colleague_title(),
    },
    ticketDetail: {
      path: '/tickets/detail/:number',
      render: ({ number }) => TicketDetailPage({ number: number! }),
      // The ticket number is the most useful thing to see in a tab, and needs no translation.
      documentTitlePart: ({ number }) => number || m.nav_tickets(),
    },
    svrPage: {
      path: '/tickets/detail/:number/svr',
      render: ({ number }) => SVRPage({ number: number! }),
      documentTitlePart: ({ number }) => [m.title_svr(), number].filter(Boolean).join(' '),
    },
    academy: { path: '/academy', render: () => AcademyPage(), documentTitlePart: () => m.academy_title() },
    courseDetail: {
      path: '/course/:courseId',
      render: ({ courseId }) => CourseDetailPage({ courseId: courseId! }),
      // The course's own name would be better, but it isn't known without a fetch the router
      // would have to await on every navigation.
      documentTitlePart: () => m.title_course(),
    },
    coursePlayer: {
      path: '/course/:courseId/play/:enrollmentId',
      render: ({ courseId, enrollmentId }) => CoursePlayerPage({ courseId: courseId!, enrollmentId: enrollmentId! }),
      documentTitlePart: () => m.title_course(),
    },
    profile: { path: '/profile', render: () => ProfilePage(), documentTitlePart: () => m.profile_title() },
    users: { path: '/users', render: () => UserManagementPage(), documentTitlePart: () => m.user_management_title() },
  },
  { routerDocumentTitle: APP_DOCUMENT_TITLE },
).init();

/** Route every permission denial falls back to — and the one path that cannot redirect to itself. */
const FALLBACK_ROUTE_PATH = '/';

/** App chrome (navbar) around whatever the shell decides to show in the content area. */
const shellFrame = (content: unknown) => html`
  <div class="h-full flex flex-col bg-base-100">
    <div class="flex-1 min-h-0 overflow-y-auto">${content}</div>
    ${AppNavbar()}
  </div>
`;

const SmartTeamsAppShell = stateful(() => {
  const currentPath = router.currentRoute.value.route.path;
  const requiredPermission = ROUTE_PERMISSION_MAP[currentPath];

  const { can: hasAccess, loading: permissionLoading } = useABAC(
    accessController,
    createPermissionPayload(requiredPermission || ''),
  );

  // Hold the page back until the gate has an answer. Rendering the outlet while the check is still
  // loading mounts the page and fires its queries before we know the user may see them — for a user
  // without the role that produced a burst of server-side authorization denials on every app open.
  if (requiredPermission && permissionLoading) {
    return shellFrame(html`
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>
    `);
  }

  if (requiredPermission && !hasAccess) {
    // Redirecting the fallback route to itself re-runs this same denial forever and leaves the user
    // on a blank screen, so say what happened instead.
    if (currentPath === FALLBACK_ROUTE_PATH) {
      return shellFrame(html`
        <div class="flex flex-col items-center justify-center h-full gap-2 px-6 text-center">
          <span class="icon-[material-symbols--lock-outline] text-4xl text-base-content/40"></span>
          <p class="font-semibold">${m.access_denied_title()}</p>
          <p class="text-sm text-base-content/60">${m.access_denied_body()}</p>
        </div>
      `);
    }
    router.goTo('tickets');
    return html``;
  }

  return shellFrame(router.outlet.value);
});

void initApp(() => {
  useEffect(() => {}, [routingStateSignal.value]); // depend on route so that we rerender when routing
  return PasswordEnforcementGate({
    appContent: () => SmartTeamsAppShell(),
    changePasswordPath: '/change-password',
    appType: 'field-engineer',
    logoSrc: smartteamLogo,
    version: APP_VERSION,
  });
});
